import React, { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import $ from "jquery"; // Assuming jQuery is available globally or via Webpacker import

// TUI may load after this module; resolve at runtime.
const getTuiImageEditor = () =>
  window.tui && window.tui.ImageEditor ? window.tui.ImageEditor : null;

const PhotoEditor = (props) => {
  const {
    editOption: initialEditOption,
    initImage,
    fileId: initialFileId,
    object: initialObject,
    updateId,
    initOnLoad,
    singleImage,
    object_class,
    callback,
    input_field,
    field, // Added based on original ajax data usage
  } = props;

  // State
  const [editOption, setEditOption] = useState(initialEditOption);
  const [editOptionShowing, setEditOptionShowing] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [imageSrc, setImageSrc] = useState(initImage);
  const [currentFileId, setCurrentFileId] = useState(initialFileId); // Use state for potentially dynamic ID
  const [dataUrl, setDataUrl] = useState("");
  const [currentObject, setCurrentObject] = useState(initialObject); // Store object state locally if updated
  const [submitted, setSubmitted] = useState(false); // Track submission status if needed

  // Refs
  const imageEditorInstance = useRef(null);
  const hiddenInputRef = useRef(null); // Ref for the hidden file input

  // --- Helper Functions ---
  const multiImage = useCallback(() => {
    // Check if currentObject exists and has a display_type
    return currentObject?.display_type && currentObject.display_type.length > 0;
  }, [currentObject]);

  const hasPhotoParent = useCallback(() => {
    return currentObject?.photographic_id !== undefined;
  }, [currentObject]);

  const objectExists = useCallback(() => {
    return currentObject?.id !== undefined;
  }, [currentObject]);

  // --- Effects ---
  useEffect(() => {
    const ensureStylesheet = (href) => {
      if (document.querySelector(`link[href="${href}"]`)) return;
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = href;
      document.head.appendChild(link);
    };

    ensureStylesheet(
      "https://uicdn.toast.com/tui-image-editor/latest/tui-image-editor.css"
    );
    ensureStylesheet(
      "https://uicdn.toast.com/tui-color-picker/latest/tui-color-picker.css"
    );
  }, []);

  // Handle dynamic fileId update
  useEffect(() => {
    if (updateId) {
      const newId = initialFileId + String(new Date().getTime());
      setCurrentFileId(newId);
      // Note: We can't directly change the ID of an already rendered input.
      // If the input *needs* this dynamic ID, it should be part of the initial render key or handled differently.
      // For now, we just store the dynamic ID for potential use in selectors/logic.
    } else {
      setCurrentFileId(initialFileId);
    }
  }, [initialFileId, updateId]);

  // Update local state if initial props change
  useEffect(() => {
    setImageSrc(initImage);
    // Only reset object if the ID changes, otherwise keep local state after updates
    if (initialObject?.id !== currentObject?.id) {
      setCurrentObject(initialObject);
    }
    // Reset other state if needed based on prop changes
  }, [initImage, initialObject]);

  // Initialize editor if initOnLoad is set
  useEffect(() => {
    if (initOnLoad) {
      handleSetEditable(); // Renamed to avoid conflict with state setter name
    }
  }, [initOnLoad]);

  // Cleanup editor instance on unmount
  useEffect(() => {
    return () => {
      if (imageEditorInstance.current) {
        imageEditorInstance.current.destroy();
        imageEditorInstance.current = null;
      }
    };
  }, []);

  // --- TUI Editor Functions ---
  const whiteTheme = () => ({
    // Re-paste the whiteTheme object content here (same as original)
    "common.bi.image":
      "https://uicdn.toast.com/toastui/img/tui-image-editor-bi.png",
    "common.bisize.width": "251px",
    "common.bisize.height": "21px",
    "common.backgroundImage": "none", // Adjusted from './img/bg.png' assuming it wasn't intended
    "common.backgroundColor": "#fff",
    "common.border": "1px solid #c1c1c1",
    "header.backgroundImage": "none",
    "header.backgroundColor": "transparent",
    "header.border": "0px",
    "loadButton.display": "none",
    "downloadButton.display": "none",
    "menu.normalIcon.color": "#8a8a8a",
    "menu.activeIcon.color": "#555555",
    "menu.disabledIcon.color": "#434343",
    "menu.hoverIcon.color": "#e9e9e9",
    "menu.iconSize.width": "24px",
    "menu.iconSize.height": "24px",
    "submenu.normalIcon.color": "#8a8a8a",
    "submenu.activeIcon.color": "#555555",
    "submenu.iconSize.width": "32px",
    "submenu.iconSize.height": "32px",
    "submenu.backgroundColor": "transparent",
    "submenu.partition.color": "#e5e5e5",
    "submenu.normalLabel.color": "#858585",
    "submenu.normalLabel.fontWeight": "normal",
    "submenu.activeLabel.color": "#000",
    "submenu.activeLabel.fontWeight": "normal",
    "checkbox.border": "1px solid #ccc",
    "checkbox.backgroundColor": "#fff",
    "range.pointer.color": "#333",
    "range.bar.color": "#ccc",
    "range.subbar.color": "#606060",
    "range.disabledPointer.color": "#d3d3d3",
    "range.disabledBar.color": "rgba(85,85,85,0.06)",
    "range.disabledSubbar.color": "rgba(51,51,51,0.2)",
    "range.value.color": "#000",
    "range.value.fontWeight": "normal",
    "range.value.fontSize": "11px",
    "range.value.border": "0",
    "range.value.backgroundColor": "#f5f5f5",
    "range.title.color": "#000",
    "range.title.fontWeight": "lighter",
    "colorpicker.button.border": "0px",
    "colorpicker.title.color": "#000",
  });

  const initEditor = useCallback(
    (src) => {
      const TuiImageEditor = getTuiImageEditor();
      if (!TuiImageEditor) {
        console.error(
          "Toast UI Image Editor (tui.ImageEditor) not found. Was it loaded?"
        );
        return;
      }
      if (imageEditorInstance.current) {
        imageEditorInstance.current.destroy(); // Destroy existing instance if re-initializing
        imageEditorInstance.current = null;
      }
      const editorElement = document.querySelector(`#editor-${currentFileId}`);
      if (!editorElement) {
        console.error(`Editor container #editor-${currentFileId} not found.`);
        return;
      }

      // Ensure container is visible before initializing
      editorElement.classList.remove("hidden");

      const editor = new TuiImageEditor(editorElement, {
        includeUI: {
          theme: whiteTheme(),
          loadImage: {
            path: src, // Use the passed src
            name: "SampleImage",
          },
          uiSize: {
            height: "600px",
          },
          initMenu: null, // Or specify a menu like 'filter', 'crop' etc.
          menuBarPosition: "right",
        },
        cssMaxWidth: 700, // Example constraints
        cssMaxHeight: 500,
        usageStatistics: false,
      });
      imageEditorInstance.current = editor;
      setEditorOpen(true);
      // TUI Editor might have issues if window is resized after init
      // window.addEventListener('resize', () => editor.ui.resizeEditor()); // Consider adding resize handler if needed
    },
    [currentFileId]
  ); // Re-run init if fileId changes

  const handleSetEditable = useCallback(() => {
    if (imageSrc) {
      // If the source is a remote URL, proxy it. Otherwise, use it directly.
      const isRemoteUrl = imageSrc.startsWith("http");
      const imageToLoad = isRemoteUrl
        ? `/image-proxy?url=${encodeURIComponent(imageSrc)}`
        : imageSrc;
      initEditor(imageToLoad);
    } else {
      console.warn("Cannot set editable without an initial image source.");
      clickFileUpload();
    }
  }, [imageSrc, initEditor]);

  const getBase64 = (file) => {
    let reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      // Update image source to preview, then initialize editor
      // This is a data URL, so it does not need the proxy.
      setImageSrc(reader.result);
      initEditor(reader.result);
    };
    reader.onerror = (error) => {
      console.error("Error reading file:", error);
    };
  };

  const addImage = (e) => {
    // No need to manually show editor container here, initEditor does it
    if (e.target.files && e.target.files.length > 0) {
      getBase64(e.target.files[0]);
    }
  };

  const clickFileUpload = () => {
    if (hiddenInputRef.current) {
      hiddenInputRef.current.click();
    }
  };

  // --- AJAX / Submission Logic ---

  const legacyImageUpload = useCallback(
    (newDataUrl) => {
      // This function seems intended to update a hidden form input *not* managed by React state.
      // This is generally discouraged. It's better to manage form state with React.
      // If absolutely necessary:
      const legacyInput = $(`#${currentFileId}`);
      if (legacyInput.length) {
        legacyInput.val(newDataUrl);
        console.warn(
          "Used legacyImageUpload to set value on non-React input:",
          `#${currentFileId}`
        );
      } else {
        console.error("Could not find legacy input:", `#${currentFileId}`);
      }
      // Also update React state if this represents the final URL
      setDataUrl(newDataUrl);
    },
    [currentFileId]
  );

  const upsertMulti = useCallback(
    (newDataUrl, eventElement) => {
      const imageId = eventElement?.getAttribute("data-image-id"); // Get image ID from button if present
      const objectId = imageId || currentObject?.id || 0; // Use button's ID, then object's ID, then 0

      let photoParams = {
        photographic_id: currentObject?.photographic_id,
        photographic_type: currentObject?.photographic_type,
        image: newDataUrl,
        display_type: currentObject?.display_type,
      };

      $.ajax({
        method: objectId ? "PUT" : "POST", // Use POST if no ID exists (create)
        url: "/admin/photos/" + (objectId || ""), // Adjust URL for POST
        data: { photo: photoParams },
      })
        .done((response) => {
          if (response.message === "saved" || response.photo) {
            // Check for photo object on create
            const savedPhoto = response.photo || {}; // Use response photo data
            // Execute callback if defined
            if (window[callback]) {
              window[callback](
                currentFileId,
                newDataUrl,
                response,
                input_field,
                eventElement
              );
            }
            // Update state: Use the *returned photo object* for multi-image scenarios
            setCurrentObject(savedPhoto);
            setDataUrl(newDataUrl); // Update dataUrl state
            setImageSrc(newDataUrl); // Ensure preview uses saved image
            setSubmitted(true); // Mark as submitted
            console.log("Multi-image saved/updated:", savedPhoto);
          } else {
            console.error("Upsert multi-image error:", response.message);
          }
        })
        .fail((xhr, status, error) => {
          console.error("Upsert multi-image AJAX failed:", status, error);
        });
    },
    [currentObject, currentFileId, callback, input_field]
  ); // Include dependencies

  const updateParent = useCallback(
    (newDataUrl, eventElement) => {
      // Determine the field name based on the fileId convention (e.g., 'object_banner_image')
      const fieldName =
        initialFileId?.split("_").slice(1).join("_") || field || "image"; // Fallback to props.field or 'image'
      let objectPath = object_class ? `${object_class}s` : null; // e.g., 'page' -> 'pages'

      if (!objectPath || !currentObject?.id) {
        console.error(
          "Cannot update parent: Missing object_class or object id.",
          { object_class, currentObject }
        );
        return;
      }
      // Handle potential pluralization inconsistencies
      if (object_class === "itinerary") objectPath = "itineraries"; // Specific override

      let parentParams = {
        [object_class]: {
          [fieldName]: newDataUrl,
        },
      };

      $.ajax({
        method: "PUT",
        url: `/admin/${objectPath}/${currentObject.id}`,
        data: parentParams,
      })
        .done((response) => {
          if (response.message === "saved" || response.entity) {
            // Check for entity for consistency
            const updatedEntity = response.entity || currentObject; // Use returned entity or assume success
            if (window[callback]) {
              window[callback](
                currentFileId,
                newDataUrl,
                response,
                input_field,
                eventElement
              );
            }
            setCurrentObject(updatedEntity); // Update local object state
            setDataUrl(newDataUrl);
            setImageSrc(newDataUrl);
            setSubmitted(true);
            console.log("Parent object updated:", updatedEntity);
          } else {
            console.error("Update parent error:", response.message);
          }
        })
        .fail((xhr, status, error) => {
          console.error("Update parent AJAX failed:", status, error);
        });
    },
    [
      currentObject,
      object_class,
      currentFileId,
      initialFileId,
      field,
      callback,
      input_field,
    ]
  ); // Dependencies

  const setFileValue = useCallback(
    (newDataUrl, eventElement) => {
      if (multiImage() && hasPhotoParent()) {
        // Hide the legacy input if it exists (though ideally it shouldn't be used)
        $(`#${currentFileId}`).hide();
        upsertMulti(newDataUrl, eventElement);
      } else if (objectExists()) {
        $(`#${currentFileId}`).hide();
        updateParent(newDataUrl, eventElement);
      } else {
        // This case implies no existing object and not a multi-image setup.
        // Fallback to updating a potentially non-React input field.
        legacyImageUpload(newDataUrl);
      }
    },
    [
      multiImage,
      hasPhotoParent,
      objectExists,
      upsertMulti,
      updateParent,
      legacyImageUpload,
      currentFileId,
    ]
  );

  const saveImage = (event) => {
    if (!imageEditorInstance.current) {
      console.error("Editor not initialized, cannot save.");
      return;
    }
    // Use TUI API to get data URL
    const newDataUrl = imageEditorInstance.current.toDataURL();

    if (input_field) {
      const input = document.querySelector(`input[name="${input_field}"]`);
      if (input) input.value = newDataUrl;
    }

    if (singleImage) {
      // Hide editor, show preview with the new image
      setEditOptionShowing(true); // Controls preview visibility based on original logic
      const editorElement = document.querySelector(`#editor-${currentFileId}`);
      if (editorElement) editorElement.classList.add("hidden");
      // Update src for preview immediately
      setImageSrc(newDataUrl);
    }

    // Update dataUrl state and trigger appropriate save action
    if (editorOpen && newDataUrl !== dataUrl) {
      // Pass the event target if needed by callbacks/save logic
      setFileValue(newDataUrl, event.target);
    } else if (!editorOpen) {
      console.warn("Save attempted but editor wasn't open.");
    } else {
      console.log("Image data unchanged, no save action triggered.");
    }
  };

  // --- Render Logic ---
  let previewElement = null;
  let openButtonElement = (
    <div
      onClick={clickFileUpload}
      className="image-editor-button"
      data-container="image-editor"
      data-image=""
      data-value="card_image"
    >
      Add Image
    </div>
  );

  const editorContainerId = `editor-${currentFileId}`;
  const hiddenInputId = `myfile${currentFileId}`;

  // Determine if preview should be shown
  if (imageSrc && (!editorOpen || editOptionShowing)) {
    openButtonElement = null; // Hide Add button if preview shown

    if (singleImage || editOption) {
      // Original logic used `editOption` here too
      previewElement = (
        <div
          className="singlePreviewWrapper"
          style={{ position: "relative", display: "inline-block" }}
        >
          <img
            className="image-preview"
            src={imageSrc}
            onClick={clickFileUpload}
            alt="Image Preview"
            style={{
              maxWidth: "200px",
              maxHeight: "200px",
              display: "block",
              borderRadius: "4px",
            }}
          />
          <div
            className="singleActionsButtons"
            style={{
              position: "absolute",
              top: "5px",
              right: "5px",
              display: "flex",
              gap: "5px",
            }}
          >
            {/* Edit Button */}
            <button
              type="button"
              className="editImage"
              onClick={handleSetEditable}
              title="Edit Image"
              style={{
                background: "rgba(0, 0, 0, 0.6)",
                color: "white",
                border: "none",
                borderRadius: "50%",
                width: "30px",
                height: "30px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <img
                src={window.IMAGES_PATH?.edit || "/assets/edit_icon.png"}
                alt="Edit"
                style={{ width: "16px", height: "16px" }}
              />
            </button>
            {/* Delete Button */}
            <button
              type="button"
              className="singleDeleteImage" // Corrected typo
              title="Delete Image"
              onClick={() => {
                // Clear local state
                setEditorOpen(false);
                setImageSrc(null);
                setDataUrl(""); // Clear saved data URL too
                // Destroy editor if open
                if (imageEditorInstance.current) {
                  imageEditorInstance.current.destroy();
                  imageEditorInstance.current = null;
                }
                // Trigger AJAX delete
                let objectId = currentObject?.id;
                if (objectId != null) {
                  let deleteUrl;
                  if (multiImage() && hasPhotoParent()) {
                    // Assumes multi-images are deleted via /admin/photos/:id
                    deleteUrl = `/admin/photos/${objectId}`;
                  } else {
                    // Attempting to nullify field on parent? Risky. Needs backend support.
                    // It's safer to handle parent updates in a dedicated way or rely on upsert only.
                    // This part needs review based on backend capabilities.
                    console.warn(
                      "Delete logic for non-multi-image parent update needs review."
                    );
                    // Example: Maybe set dataUrl to 'DELETE' and trigger updateParent?
                    // updateParent('DELETE', null); // Needs backend handler
                    return; // Avoid direct deletion unless intended
                  }

                  $.ajax({
                    method: "DELETE",
                    url: deleteUrl,
                    // Send necessary params if required by backend
                    // data: { class: object_class, field: field } // Original params if needed
                  })
                    .done((response) => {
                      console.log("Image deleted:", response);
                      // Optionally update state if parent object returns changed
                      if (response.entity) setCurrentObject(response.entity);
                      // Trigger callback if needed
                      if (window[callback])
                        window[callback](
                          currentFileId,
                          null,
                          response,
                          input_field,
                          null
                        );
                    })
                    .fail((xhr, status, error) => {
                      console.error("Delete AJAX failed:", status, error);
                    });
                } else {
                  console.log("Deleted unsaved image preview.");
                }
              }}
              style={{
                background: "rgba(220, 53, 69, 0.8)",
                color: "white",
                border: "none",
                borderRadius: "50%",
                width: "30px",
                height: "30px",
                cursor: "pointer",
                fontWeight: "bold",
                fontSize: "16px",
              }}
            >
              &times;
            </button>
          </div>
        </div>
      );
    } else {
      // Default preview for non-single/non-editOption cases
      previewElement = (
        <img
          className="image-preview"
          src={imageSrc}
          onClick={clickFileUpload}
          alt="Image Preview"
        />
      );
    }
  } else if (editorOpen && !editOptionShowing) {
    // Editor is open, hide preview and "Add Image" button
    previewElement = null;
    openButtonElement = null;
  }

  return (
    <div>
      {editorOpen && (
        <div style={{ marginBottom: "10px" }}>
          <button
            type="button"
            onClick={saveImage}
            style={{
              padding: "10px 20px",
              fontSize: "16px",
              cursor: "pointer",
              backgroundColor: "#007bff",
              color: "white",
              border: "none",
              borderRadius: "5px",
              fontWeight: "bold",
            }}
            title="Save Image"
          >
            Save Changes
          </button>
        </div>
      )}
      {/* TUI Editor Container */}
      <div
        id={editorContainerId}
        className={editorOpen && !editOptionShowing ? "" : "hidden"}
      />
      {/* Hidden File Input */}
      <input
        type="file"
        id={hiddenInputId}
        ref={hiddenInputRef} // Attach ref
        name="myfile"
        className="hidden"
        onChange={addImage}
        accept="image/*" // Specify accepted file types
      />
      {/* "Add Image" Button / Trigger */}
      {openButtonElement}
      {/* Preview Area */}
      {previewElement}
      {/* Optional: Original hidden input for legacy form submission fallback */}
      {/* Only render if absolutely needed and legacyImageUpload is used */}
      {/* <input type="hidden" id={currentFileId} name={input_field || initialFileId} /> */}
    </div>
  );
};

PhotoEditor.propTypes = {
  editOption: PropTypes.bool,
  editOptionShowing: PropTypes.bool, // Should this be controlled internally? Seems like it from original
  initImage: PropTypes.string,
  fileId: PropTypes.string.isRequired,
  object: PropTypes.object, // Shape depends heavily on usage
  updateId: PropTypes.bool,
  initOnLoad: PropTypes.bool,
  singleImage: PropTypes.bool,
  object_class: PropTypes.string, // Needed for AJAX calls
  callback: PropTypes.string, // Name of a global callback function
  input_field: PropTypes.string, // Name attribute for the hidden input (if used)
  field: PropTypes.string, // Field name on parent object (used in delete)
  // Added props based on usage:
  class: PropTypes.string, // Passed to delete AJAX in original? Seems redundant with object_class
};

PhotoEditor.defaultProps = {
  editOption: false,
  editOptionShowing: false, // Default internal state might be better
  initImage: null,
  object: {},
  updateId: false,
  initOnLoad: false,
  singleImage: false,
  object_class: "",
  callback: null,
  input_field: null,
  field: null,
};

export default PhotoEditor;
